import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Home,
  Loader2,
  BookOpen,
  Eye,
  MousePointer,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { getEbookMetrics } from "@/api/aimeansbusiness";
import { useAuth } from "@/context/AuthContext";

const AdminEbookMetrics = () => {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEbookMetrics();
      if (response.ok) {
        setMetrics(response);
      } else {
        setError("Failed to load metrics");
      }
    } catch (err) {
      console.error("Failed to fetch ebook metrics:", err);
      setError(err.message || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMetrics();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Ebook Offer Metrics
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMetrics}
            className="border-slate-700 text-slate-300"
            data-testid="refresh-metrics-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Disclaimer Notice */}
        <div className="mb-8 p-4 rounded-xl bg-blue-950/30 border border-blue-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-300 mb-1">Metrics Represent Intent, Not Purchases</h3>
              <p className="text-blue-300/80 text-sm leading-relaxed">
                These metrics track user engagement and outbound intent only. They do <strong>not</strong> indicate 
                or claim that a book was purchased or that a promo code was redeemed. Use this data to measure 
                offer effectiveness and user interest.
              </p>
            </div>
          </div>
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={fetchMetrics} variant="outline">
              Try Again
            </Button>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Offer Views"
                value={metrics?.summary?.total_offer_views || 0}
                description="Users who saw the ebook CTA"
                icon={Eye}
                color="blue"
              />
              <MetricCard
                title="Modal Opens"
                value={metrics?.summary?.total_modal_opens || 0}
                description="Users who clicked to see promo code"
                icon={MousePointer}
                color="purple"
              />
              <MetricCard
                title="Outbound Clicks"
                value={metrics?.summary?.total_outbound_clicks || 0}
                description="Users who clicked through to B&N"
                icon={ExternalLink}
                color="green"
              />
              <MetricCard
                title="Click-Through Rate"
                value={`${metrics?.summary?.click_through_rate || 0}%`}
                description="Outbound clicks ÷ modal opens"
                icon={TrendingUp}
                color="amber"
              />
            </div>

            {/* Detailed Metrics */}
            <div className="glass p-6 rounded-xl mb-8">
              <h2 className="font-heading text-lg font-semibold text-white mb-6">
                Detailed Event Breakdown
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Event Type</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">Total Count</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">Unique Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    <MetricRow
                      label="Ebook Offer Shown"
                      description="CTA became visible in User Portal"
                      count={metrics?.metrics?.ebook_offer_shown?.count || 0}
                      uniqueUsers={metrics?.metrics?.ebook_offer_shown?.unique_users || 0}
                    />
                    <MetricRow
                      label="Ebook Modal Opened"
                      description="User clicked CTA and saw promo code"
                      count={metrics?.metrics?.ebook_modal_opened?.count || 0}
                      uniqueUsers={metrics?.metrics?.ebook_modal_opened?.unique_users || 0}
                    />
                    <MetricRow
                      label="Ebook Outbound Click"
                      description="User confirmed and was redirected"
                      count={metrics?.metrics?.ebook_outbound_click?.count || 0}
                      uniqueUsers={metrics?.metrics?.ebook_outbound_click?.unique_users || 0}
                    />
                  </tbody>
                </table>
              </div>
            </div>

            {/* Metric Definitions */}
            <div className="glass p-6 rounded-xl">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                Metric Definitions
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex gap-3">
                  <code className="text-primary bg-navy-950 px-2 py-1 rounded text-xs">ebook_offer_shown</code>
                  <span className="text-slate-400">User became eligible and saw the ebook CTA in their portal</span>
                </div>
                <div className="flex gap-3">
                  <code className="text-primary bg-navy-950 px-2 py-1 rounded text-xs">ebook_modal_opened</code>
                  <span className="text-slate-400">User clicked CTA and saw the promo code in the modal</span>
                </div>
                <div className="flex gap-3">
                  <code className="text-primary bg-navy-950 px-2 py-1 rounded text-xs">ebook_outbound_click</code>
                  <span className="text-slate-400">User clicked "Okay" and was redirected to Barnes & Noble</span>
                </div>
                <div className="flex gap-3">
                  <code className="text-primary bg-navy-950 px-2 py-1 rounded text-xs">click_through_rate</code>
                  <span className="text-slate-400">Outbound clicks ÷ modal opens (measures confirmation intent)</span>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const MetricCard = ({ title, value, description, icon: Icon, color }) => {
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    green: "bg-green-500/20 text-green-400 border-green-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30"
  };

  return (
    <div className="glass p-6 rounded-xl" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-heading font-bold text-white mb-1">{value}</p>
      <p className="text-white font-medium text-sm mb-1">{title}</p>
      <p className="text-slate-500 text-xs">{description}</p>
    </div>
  );
};

const MetricRow = ({ label, description, count, uniqueUsers }) => (
  <tr className="border-b border-slate-800 hover:bg-slate-800/30">
    <td className="py-4 px-4">
      <p className="text-white font-medium text-sm">{label}</p>
      <p className="text-slate-500 text-xs">{description}</p>
    </td>
    <td className="py-4 px-4 text-right">
      <span className="text-white font-semibold">{count}</span>
    </td>
    <td className="py-4 px-4 text-right">
      <span className="text-slate-400">{uniqueUsers}</span>
    </td>
  </tr>
);

export default AdminEbookMetrics;
